import React, { useState, useEffect } from "react"
import { useDispatch } from "react-redux"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { styled } from "@mui/material/styles"
import { BottomSheetAction, PathInfo } from "types/general.types"
import { CLOSE_FRIENDS, EDIT, FULFILLED, ITEM, LIST, MAKE_WISH_TRUE } from "constants/index"
import {
  HANDLE_DELETE_ITEM,
  HANDLE_EDIT_ITEM,
  HANDLE_COPY_ITEM_LINK,
  HANDLE_TRANSFER_ITEM_TO_OTHER_LISTS,
  OPEN_ITEM_ACTIONS_BOTTOM_SHEET,
  ITEM_COMMENT_BUTTON_CLICKED,
  ITEM_LIKE_COUNT_CLICKED
} from "constants/events"
import {
  useDeleteWishItemMutation,
  useEditWishItemMutation,
  useRollbackTrueWishMutation
} from "appRedux/slices/wishItemApiSlice"
import {
  updateCurrentItemIndex,
  updateCurrentItemInfo,
  updateShowCommentsBottomSheet,
  updateShowGeniesList,
  updateShowLikedByBottomSheet,
  updateShowRollbackConfirmationDialog,
  updateShowSelectGeniesBottomSheet,
  updateWishListViewMode
} from "appRedux/slices/wishListSlice"
import { updateItemFormFields, updateItemFormMode, updateShowChooseListsDropSheet } from "appRedux/slices/itemFormSlice"
import { useAppSelector } from "appRedux/hooks"
import { AppDispatch } from "appRedux/store"
import { wishItemRequestSchema } from "validations/wishItem"
import useEventListener from "hooks/useEventListener"
import useCopyToClipboard from "hooks/useCopyToClipboard"
import useWishItem from "hooks/useWishItem"
import { useAuth } from "context/AuthContext"
import { PATH_INFOS } from "utils/navigatorUtils"
import { handlePremiumOnboarding } from "utils/componentsUtils"
import Slider from "components/atoms/Slider"
import Dialog from "components/atoms/Dialog"
import WishItemSlide from "components/molecules/WishItemSlide"
import WishItemActionsBottomSheet from "components/organisms/WishItemActionsBottomSheet"
import SelectListDropSheetToAddItems from "components/organisms/SelectListDropSheetToAddItems"
import CommentsBottomSheet from "components/organisms/WishItemBottomSection/CommentsBottomSheet"
import LikedByBottomSheet from "components/organisms/WishItemBottomSection/LikedByBottomSheet"
import CreateNewListOverlay from "components/templates/WishItemForm/CreateNewListOverlay"
import WarningRoundedIcon from "components/atoms/_icons/WarningRoundedIcon"
import withRoleBasedPermission from "components/HOCs/withRoleBasedPermission"
import SelectGenies from "components/organisms/WishItemBottomSection/TrueWishSection/SelectGenies"
import GeniesList from "components/organisms/WishItemBottomSection/TrueWishSection/GeniesList"

const SelectGeniesWithRoleBasedPermission = withRoleBasedPermission(SelectGenies)

function renderWishItem(itemInfo: WishItem, index: number) {
  return (
    <WishItemSlide itemInfo={itemInfo} index={index}/>
  )
}

type WishListSliderViewProps = {
  list: WishItem[],
  showProgressBar: boolean;
  moreButtonActions: BottomSheetAction[];
}

export default function WishListSliderView(props: WishListSliderViewProps) {
  const { list, showProgressBar, moreButtonActions } = props

  const [showItemActionsBottomSheet, setShowItemActionsBottomSheet] = useState<boolean>(false)
  const [showDeleteItemConfirmationDialog, setShowDeleteItemConfirmationDialog] = useState<boolean>(false)
  const [currentItemId, setCurrentItemId] = useState<number | null>(null)

  const wishListState = useAppSelector((state) => state.wishList)
  const showChooseListsDropSheet = useAppSelector((state) => state.itemForm.showChooseListsDropSheet)
  const showCreateNewListOverlay = useAppSelector((state) => state.itemForm.showCreateNewListOverlay)
  const currentPageInfo = useAppSelector((state) => state.global.currentPageInfo)
  const userProfile = useAppSelector((state) => state.user.profile)
  const showCommentsBottomSheet = useAppSelector(state => state.wishList.showCommentsBottomSheet)
  const showLikedByBottomSheet = useAppSelector(state => state.wishList.showLikedByBottomSheet)
  const showRollbackConfirmationDialog = useAppSelector(state => state.wishList.showRollbackConfirmationDialog)
  const showSelectGeniesBottomSheet = useAppSelector((state) => state.wishList.showSelectGeniesBottomSheet)
  const genies = useAppSelector((state) => (state.wishList.genies && wishListState?.itemInfo?.id) ? state.wishList.genies[wishListState?.itemInfo?.id] : [])
  const showGeniesList = useAppSelector((state) => state.wishList.showGeniesList)
  const isPremium = useAppSelector((state) => state.user.profile?.isPremium)

  const [triggerDeleteWishItem, deleteResult] = useDeleteWishItemMutation()
  const [triggerRollbackTrueWishChanges, rollbackTrueWishChangesResult] = useRollbackTrueWishMutation()
  const [triggerEditWishItem] = useEditWishItemMutation()

  const { user } = useAuth()
  const { copyToClipboard } = useCopyToClipboard()
  const { isFetching: wishItemIsLoading } = useWishItem(currentItemId)

  const { t } = useTranslation()
  const dispatch = useDispatch() as AppDispatch
  const navigate = useNavigate()

  useEventListener(document, OPEN_ITEM_ACTIONS_BOTTOM_SHEET, handleOpenItemActionsBottomSheet)

  useEventListener(document, HANDLE_DELETE_ITEM, handleDeleteItemButtonClicked)

  useEventListener(document, HANDLE_EDIT_ITEM, handleEditItemButtonClicked)

  useEventListener(document, HANDLE_TRANSFER_ITEM_TO_OTHER_LISTS, handleTransferToOtherListsButtonClicked)

  useEventListener(document, HANDLE_COPY_ITEM_LINK, handleCopyItemLink)

  useEventListener(document, ITEM_COMMENT_BUTTON_CLICKED, handleOpenCommentsBottomSheet)

  useEventListener(document, ITEM_LIKE_COUNT_CLICKED, handleOpenLikedByBottomSheet)

  useEffect(() => {
    if (deleteResult.status === FULFILLED) {
      setShowDeleteItemConfirmationDialog(false)
      handleWishItemDeletedSuccessfully()
    }
  }, [deleteResult])

  useEffect(() => {
    if (rollbackTrueWishChangesResult.status === FULFILLED) {
      handleCloseRollbackTrueWishChangesConfirmationDialog()
    }
  }, [rollbackTrueWishChangesResult])

  function handleOpenItemActionsBottomSheet() {
    setShowItemActionsBottomSheet(true)
  }

  function handleDeleteItemButtonClicked() {
    setShowItemActionsBottomSheet(false)
    setShowDeleteItemConfirmationDialog(true)
  }

  function handleEditItemButtonClicked(event: React.MouseEvent<HTMLButtonElement>) {
    setShowItemActionsBottomSheet(false)
    if (!wishListState.itemInfo) return
    const { id, name, caption, url, image, wishlists, price, currency } = wishListState.itemInfo

    if (!isPremium && wishlists.some(list => list.privacy === CLOSE_FRIENDS)) {
      handlePremiumOnboarding(event, CLOSE_FRIENDS, dispatch)
      return
    }

    const wishlistIds = wishlists.map(list => list.id)

    dispatch(updateItemFormMode(EDIT))
    dispatch(updateItemFormFields({
      name,
      caption: caption,
      url: url,
      image: image || "",
      wishlistIds,
      price,
      currency
    }))
    navigate(`/user/${user?.handle}/item/${id}/edit`)
  }

  function handleTransferToOtherListsButtonClicked() {
    setShowItemActionsBottomSheet(false)
    handleShowChooseListsDropSheet()
  }

  async function handleCopyItemLink() {
    if (!userProfile) return
    const pageLink = getItemUrl(userProfile.handle, currentPageInfo)
    await copyToClipboard(pageLink)
  }

  function handleOpenCommentsBottomSheet() {
    if (showCommentsBottomSheet) return
    dispatch(updateShowCommentsBottomSheet(true))
  }

  function handleCloseCommentsBottomSheet() {
    if (!showCommentsBottomSheet) return
    dispatch(updateShowCommentsBottomSheet(false))
  }

  function handleOpenLikedByBottomSheet() {
    if (showLikedByBottomSheet) return
    dispatch(updateShowLikedByBottomSheet(true))
  }

  function handleCloseLikedByBottomSheet() {
    if (!showLikedByBottomSheet) return
    dispatch(updateShowLikedByBottomSheet(false))
  }

  function getItemUrl(loggedInUserHandle: string, currentPageInfo: PathInfo) {
    if (!currentPageInfo) return ""
    let username
    if (currentPageInfo?.dynamicPathParts?.handle) {
      username = currentPageInfo.dynamicPathParts.handle
    } else {
      username = loggedInUserHandle
    }
    let url = window.location.origin + "/public/user/" + username
    url += `/item/${wishListState.itemInfo?.id}`
    return url
  }

  function handleWishItemDeletedSuccessfully() {
    dispatch(updateCurrentItemInfo(null))
    if (!wishListState.listInfo || currentPageInfo?.path === PATH_INFOS.viewItem.path) {
      navigate(`/user/${user?.handle}`)
      return
    }
    if (wishListState?.listInfo?.listItems && wishListState?.listInfo?.listItems?.length > 0) {
      if (wishListState.currentItemIndex < wishListState?.listInfo?.listItems?.length) {
        changeCurrentWishItem(wishListState.currentItemIndex, wishListState.currentItemIndex)
      } else {
        changeCurrentWishItem(wishListState.currentItemIndex - 1, wishListState.currentItemIndex - 1)
      }
    } else {
      dispatch(updateWishListViewMode(LIST))
    }
  }

  function handleOnCurrentSlideChange(currentSlideIndex: number) {
    changeCurrentWishItem(currentSlideIndex, currentSlideIndex)
  }

  function changeCurrentWishItem(currentSlideIndex: number, itemIndex: number) {
    if (!wishListState.listInfo || !wishListState.listInfo.listItems) return
    const item = wishListState.listInfo.listItems[itemIndex]
    dispatch(updateCurrentItemIndex(currentSlideIndex))
    if (item) {
      dispatch(updateCurrentItemInfo({ ...item, wishlists: [] }))
    }
    setCurrentItemId(item?.id)
  }

  function handleCloseItemActionsBottomSheet() {
    setShowItemActionsBottomSheet(false)
  }

  function handleCloseDeleteItemConfirmationDialog() {
    setShowDeleteItemConfirmationDialog(false)
  }

  function handleDeleteItem() {
    if (!wishListState.itemInfo) return
    triggerDeleteWishItem(wishListState.itemInfo.id)
  }

  function handleShowChooseListsDropSheet() {
    dispatch(updateShowChooseListsDropSheet(true))
  }

  function handleCloseChooseListsDropSheet() {
    dispatch(updateShowChooseListsDropSheet(false))
  }

  async function handleSubmitSelectedListsForItem(lists: number[]) {
    handleCloseChooseListsDropSheet()
    const item = wishListState.itemInfo
    if (!item) return
    await handleEditWishItem({
      ...item,
      image: item.image || "",
      wishlistIds: lists,
      price: item.price || "",
      currency: ""
    })
  }

  async function handleEditWishItem(value: WishItemRequestPayload) {
    if (!currentPageInfo?.dynamicPathParts) return
    const formData = new FormData()
    try {
      const parsedValue = await wishItemRequestSchema.parseAsync(value)
      Object.entries(parsedValue).forEach(([key, val]) => {
        if (key === "image" && typeof val === "string") return
        formData.append(key, val)
      })
      triggerEditWishItem({
        id: wishListState?.itemInfo?.id,
        body: formData
      })
    } catch (err) {
      console.error(err)
    }
  }

  function getCurrentListsForWishItem() {
    const item = wishListState.itemInfo
    if (!item) return []
    return item?.wishlists?.map(l => l.id)
  }

  function handleRollbackTrueWishChanges() {
    if (!wishListState.itemInfo) return
    triggerRollbackTrueWishChanges(wishListState.itemInfo.id)
  }

  function handleCloseRollbackTrueWishChangesConfirmationDialog() {
    dispatch(updateShowRollbackConfirmationDialog(false))
  }

  function handleCloseSelectGeniesBottomSheet() {
    dispatch(updateShowSelectGeniesBottomSheet(false))
  }

  function handleCloseGeniesList() {
    dispatch(updateShowGeniesList(false))
  }

  return (
    <Container id="wishlist-slider-view">
      <Gradient/>
      <Slider
        list={list}
        showProgressBar={showProgressBar}
        isLoading={wishItemIsLoading}
        render={renderWishItem}
        onChange={handleOnCurrentSlideChange}
      />
      <WishItemActionsBottomSheet
        show={showItemActionsBottomSheet}
        actions={moreButtonActions}
        onOpen={() => {
        }}
        onClose={handleCloseItemActionsBottomSheet}
      />
      {wishListState.itemInfo && <CommentsBottomSheet
          show={showCommentsBottomSheet}
          id={wishListState.itemInfo?.id}
          onOpen={handleOpenCommentsBottomSheet}
          onClose={handleCloseCommentsBottomSheet}
      />}
      {wishListState.itemInfo && <LikedByBottomSheet
          show={showLikedByBottomSheet}
          id={wishListState.itemInfo?.id}
          onOpen={handleOpenLikedByBottomSheet}
          onClose={handleCloseLikedByBottomSheet}
      />}
      {wishListState.itemInfo && <SelectGeniesWithRoleBasedPermission
          resource={ITEM}
          action={MAKE_WISH_TRUE}
          show={showSelectGeniesBottomSheet}
          id={wishListState.itemInfo?.id}
          onOpen={() => {
          }}
          onClose={handleCloseSelectGeniesBottomSheet}
      />}
      <GeniesList
        show={showGeniesList}
        genies={genies}
        onOpen={() => {
        }}
        onClose={handleCloseGeniesList}
      />
      <Dialog
        message={t("items.deleteConfirmationQuestion")}
        icon={<WarningRoundedIcon/>}
        buttons={[
          {
            label: t("general.yes"),
            mode: "white",
            loading: deleteResult.isLoading,
            onClick: handleDeleteItem
          },
          {
            label: t("general.no"),
            mode: "primary",
            loading: false,
            disabled: deleteResult.isLoading,
            onClick: handleCloseDeleteItemConfirmationDialog
          }
        ]}
        open={showDeleteItemConfirmationDialog}
        disableBackdropClose={true}
        onClose={handleCloseDeleteItemConfirmationDialog}
      />
      <Dialog
        message={t("items.rollbackTureWishChangesConfirmationQuestion")}
        icon={<WarningRoundedIcon/>}
        buttons={[
          {
            label: t("general.yes"),
            mode: "white",
            loading: rollbackTrueWishChangesResult.isLoading,
            onClick: handleRollbackTrueWishChanges
          },
          {
            label: t("general.no"),
            mode: "primary",
            loading: false,
            disabled: rollbackTrueWishChangesResult.isLoading,
            onClick: handleCloseRollbackTrueWishChangesConfirmationDialog
          }
        ]}
        open={showRollbackConfirmationDialog}
        disableBackdropClose={true}
        onClose={handleCloseRollbackTrueWishChangesConfirmationDialog}
      />

      <SelectListDropSheetToAddItems
        show={showChooseListsDropSheet}
        CTALabel={t("general.submit")}
        defaultSelectedLists={getCurrentListsForWishItem()}
        onOpen={handleShowChooseListsDropSheet}
        onClose={handleCloseChooseListsDropSheet}
        onSubmit={handleSubmitSelectedListsForItem}
      />
      <CreateNewListOverlay
        show={showCreateNewListOverlay}
      />
    </Container>
  )
}

const Container = styled("div")(() => ({
  height: "calc(100vh)",
  marginTop: "-96px",
  backgroundColor: "#000",
  position: "relative",

  "@supports (height: 100dvh)": {
    height: "calc(100dvh)"
  }
}))

const Gradient = styled("div")(() => ({
  width: "100vw",
  maxWidth: "var(--max-container-width)",
  height: "100px",
  background: "linear-gradient(180deg, #000000, transparent)",
  position: "absolute",
  left: 0,
  top: 0,
  zIndex: 2
}))
